import type React from 'react';
import { useEffect, useRef } from 'react';

import { useFormContext } from 'react-hook-form';

import { Text } from '@change/ds-core-ui';
import { ErrorMessage, TextField } from '@change/ds-forms';
import { cn } from '@change/ds-utils';

type CharacterCounterProps = {
	current: number;
	max: number;
	hasError?: boolean;
};

const CharacterCounter: React.FC<CharacterCounterProps> = ({ current, max, hasError }) => (
	<Text
		variant="body"
		size="x-small"
		className={cn('text-right', hasError ? 'text-typography-error' : 'text-typography-weak')}
	>
		{hasError ? (
			<>
				<span className="font-bold">{current}</span>
				<span>/{max}</span>
			</>
		) : (
			<>
				{current}/{max}
			</>
		)}
	</Text>
);

export type TextFieldWithCounterProps = {
	name: string;
	label: string;
	supportingText: string;
	maxLength?: number;
	currentLength?: number;
	inputElementType?: 'input' | 'textarea';
	inputClassName?: string;
	testId: string;
	qaId: string;
	displayCounter?: boolean;
};

function useFieldValidation(name: string, maxLength: number | undefined, currentLength: number) {
	const formContext = useFormContext();
	const hasLengthError = maxLength ? currentLength > maxLength : false;
	const fieldError = formContext.formState.errors[name];
	const zodErrorMessage = typeof fieldError?.message === 'string' ? fieldError.message : undefined;
	const hasError = hasLengthError || !!zodErrorMessage;
	const errorMessage = hasLengthError ? `Maximum ${maxLength} characters allowed` : zodErrorMessage;

	return { hasError, errorMessage };
}

export const TextFieldWithCounter: React.FC<TextFieldWithCounterProps> = ({
	name,
	label,
	supportingText,
	maxLength,
	currentLength,
	inputElementType = 'input',
	inputClassName,
	testId,
	qaId,
	displayCounter = true,
}) => {
	const formContext = useFormContext();
	const value = String(formContext.watch(name) ?? '');
	const actualCurrentLength = currentLength ?? value.length;
	const inputOpts = inputClassName ? { className: inputClassName } : {};
	const { hasError, errorMessage } = useFieldValidation(name, maxLength, actualCurrentLength);
	const prevHasErrorRef = useRef(false);

	// Trigger validation on value change when clearing an error
	useEffect(() => {
		// Only trigger if we previously had an error (field was touched)
		if (prevHasErrorRef.current) {
			void formContext.trigger(name);
		}
		prevHasErrorRef.current = hasError;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, hasError]);

	return (
		<div className="flex flex-col gap-1" data-qa={qaId}>
			<TextField
				name={name}
				label={label}
				labelIsVisible
				inputElementType={inputElementType}
				inputOptions={inputOpts}
				data-testid={testId}
				data-qa={qaId}
				size="large"
				boldLabel
				isInvalid={false}
				errorMessage=""
			/>

			{/* Custom error message and character counter on the same row */}
			<div className="flex w-full items-start justify-between gap-2">
				<div className="flex flex-1 items-center gap-1">
					{errorMessage ? (
						<ErrorMessage size="large">
							<>{errorMessage}</>
						</ErrorMessage>
					) : (
						<Text variant="body" size="x-small" className="text-typography-weak">
							{supportingText}
						</Text>
					)}
				</div>
				{displayCounter && maxLength && (
					<CharacterCounter current={actualCurrentLength} max={maxLength} hasError={hasError} />
				)}
			</div>
		</div>
	);
};
